import { platformApiFetch } from '@/services/platformApi/client';
import type {
  AdminScope,
  ApiEnvelope,
  CreateMachineApiKeyResponse,
  CreateMachinePrincipalResponse,
  DeleteMachineApiKeyResponse,
  ListMachineAccessResponse,
} from '@/types/api';

type AdminMachineAccessAction =
  | {
      action: 'list';
      requestId?: string;
    }
  | {
      action: 'createPrincipal';
      payload: {
        name: string;
        description?: string | null;
        allowedScopes: AdminScope[];
      };
      requestId?: string;
    }
  | {
      action: 'createApiKey';
      payload: {
        machinePrincipalId: string;
        name: string;
        allowedScopes?: AdminScope[];
        expiresAt?: string | null;
      };
      requestId?: string;
      idempotencyKey?: string;
    }
  | {
      action: 'revokeApiKey';
      payload: {
        apiKeyId: string;
      };
      requestId?: string;
      idempotencyKey?: string;
    }
  | {
      action: 'deleteApiKey';
      payload: {
        apiKeyId: string;
      };
      requestId?: string;
      idempotencyKey?: string;
    };

const invokeMachineAccess = async <T>(action: AdminMachineAccessAction): Promise<T> => {
  switch (action.action) {
    case 'list':
      return platformApiFetch<T>('/v1/admin/machine-access');
    case 'createPrincipal':
      return platformApiFetch<T>('/v1/admin/machine-access/principals', { method: 'POST', body: JSON.stringify(action.payload) });
    case 'createApiKey':
      return platformApiFetch<T>('/v1/admin/machine-access/api-keys', { method: 'POST', body: JSON.stringify(action.payload) });
    case 'revokeApiKey':
      return platformApiFetch<T>(`/v1/admin/machine-access/api-keys/${action.payload.apiKeyId}/revoke`, { method: 'POST' });
    case 'deleteApiKey':
      return platformApiFetch<T>(`/v1/admin/machine-access/api-keys/${action.payload.apiKeyId}`, { method: 'DELETE' });
  }
};

const buildRequestId = () => crypto.randomUUID();
const buildIdempotencyKey = (prefix: string) => `${prefix}:${crypto.randomUUID()}`;

export const listMachineAccess = async () => invokeMachineAccess<ListMachineAccessResponse>({
  action: 'list',
  requestId: buildRequestId(),
});

export const createMachinePrincipal = async (payload: {
  name: string;
  description?: string | null;
  allowedScopes: AdminScope[];
}) => invokeMachineAccess<CreateMachinePrincipalResponse>({
  action: 'createPrincipal',
  payload,
  requestId: buildRequestId(),
});

export const createMachineApiKey = async (payload: {
  machinePrincipalId: string;
  name: string;
  allowedScopes?: AdminScope[];
  expiresAt?: string | null;
}) => invokeMachineAccess<CreateMachineApiKeyResponse>({
  action: 'createApiKey',
  payload,
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-machine-access:create-key:${payload.machinePrincipalId}`),
});

export const revokeMachineApiKey = async (apiKeyId: string) => invokeMachineAccess<{ apiKeyId: string; revokedAt: string; auditEventId: string }>({
  action: 'revokeApiKey',
  payload: { apiKeyId },
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-machine-access:revoke-key:${apiKeyId}`),
});

export const deleteMachineApiKey = async (apiKeyId: string) => invokeMachineAccess<DeleteMachineApiKeyResponse>({
  action: 'deleteApiKey',
  payload: { apiKeyId },
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-machine-access:delete-key:${apiKeyId}`),
});